import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ListChecks, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { trpc } from '@/lib/trpc';
import { formatCurrency, formatDate } from '@/lib/utils';
import { showErrorToast, showSuccessToast } from '@/hooks/use-mutation-with-toast';

interface AllocatedInvoice {
  id: string;
  invoiceNumber: string;
  currency: string;
  total: number;
  balanceDue: number;
  status: string;
}

export interface AllocationRow {
  id: string;
  amount: number;
  createdAt: Date | string;
  invoice: AllocatedInvoice | null;
  supplierInvoice: AllocatedInvoice | null;
}

export interface AllocatablePayment {
  id: string;
  type: string;
  currency: string;
  amount: number;
  allocated: number;
  unallocated: number;
  isVoided: boolean;
  tradingCustomerId: string | null;
  supplierId: string | null;
  allocations: AllocationRow[];
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/** Where a payment went: its allocations, the on-account remainder, and a form to apply the remainder. */
export function PaymentAllocationsCard({ payment }: { payment: AllocatablePayment }) {
  const isCustomer = payment.type === 'CUSTOMER_PAYMENT';
  const side = isCustomer ? 'CUSTOMER' : 'SUPPLIER';
  const partyId = isCustomer ? payment.tradingCustomerId : payment.supplierId;
  const invoiceHref = (id: string) => (isCustomer ? `/trading/finance/invoices/${id}` : `/trading/finance/supplier-invoices/${id}`);
  const canAllocate = !payment.isVoided && payment.unallocated > 0.005 && !!partyId;

  const [applying, setApplying] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const utils = trpc.useUtils();
  const open = trpc.tradingPayment.openInvoices.useQuery({ side, partyId: partyId ?? '' }, { enabled: applying && !!partyId });
  const allocate = trpc.tradingPayment.allocate.useMutation({
    onSuccess: (rows) => {
      showSuccessToast(`${rows.length} fatura eşleştirildi / invoices allocated`);
      setApplying(false);
      setDraft({});
      utils.tradingPayment.byId.invalidate(payment.id);
      utils.tradingPayment.list.invalidate();
    },
    onError: (e) => showErrorToast(e.message),
  });

  const candidates = (open.data ?? []).filter((i) => i.currency.toUpperCase() === payment.currency.toUpperCase());
  const list = Object.entries(draft)
    .map(([invoiceId, v]) => ({ invoiceId, amount: Number(v) }))
    .filter((a) => Number.isFinite(a.amount) && a.amount > 0);
  const total = round2(list.reduce((s, a) => s + a.amount, 0));
  const valid =
    list.length > 0 &&
    total <= payment.unallocated + 0.005 &&
    list.every((a) => {
      const inv = candidates.find((c) => c.id === a.invoiceId);
      return inv ? a.amount <= Number(inv.balanceDue) + 0.005 : false;
    });

  return (
    <div className="rounded-lg border bg-card shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
        <div>
          <h3 className="flex items-center gap-2 font-semibold">
            <ListChecks className="h-4 w-4" />
            Fatura eşleştirmesi / Allocations
          </h3>
          <p className="text-xs text-muted-foreground">
            Eşleşen {formatCurrency(payment.allocated, payment.currency)} · Avans {formatCurrency(payment.unallocated, payment.currency)}
          </p>
        </div>
        {canAllocate && !applying && (
          <Button size="sm" variant="outline" className="h-8" onClick={() => setApplying(true)}>
            Faturalara uygula / Apply to invoices
          </Button>
        )}
      </div>

      {payment.allocations.length === 0 ? (
        <p className="px-4 py-3 text-sm text-muted-foreground">
          {payment.unallocated > 0.005 ? 'Tutarın tamamı cari hesapta avans. / The whole amount is on account.' : '—'}
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-[11px] uppercase tracking-wide text-muted-foreground">
              <th className="px-4 py-1.5 font-medium">Fatura / Invoice</th>
              <th className="px-4 py-1.5 font-medium">Durum / Status</th>
              <th className="px-4 py-1.5 text-right font-medium">Açık / Open</th>
              <th className="px-4 py-1.5 text-right font-medium">Uygulanan / Applied</th>
              <th className="px-4 py-1.5 font-medium">Tarih / Date</th>
            </tr>
          </thead>
          <tbody>
            {payment.allocations.map((a) => {
              const inv = a.invoice ?? a.supplierInvoice;
              return (
                <tr key={a.id} className="border-b last:border-b-0">
                  <td className="px-4 py-1.5">
                    {inv ? (
                      <Link to={invoiceHref(inv.id)} className="font-mono text-xs hover:underline">
                        {inv.invoiceNumber}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-1.5 text-xs">{inv?.status ?? '—'}</td>
                  <td className="px-4 py-1.5 text-right tabular-nums">{inv ? formatCurrency(inv.balanceDue, inv.currency) : '—'}</td>
                  <td className="px-4 py-1.5 text-right tabular-nums font-medium">{formatCurrency(a.amount, payment.currency)}</td>
                  <td className="px-4 py-1.5 text-xs text-muted-foreground">{formatDate(a.createdAt)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {applying && (
        <div className="border-t px-4 py-3">
          <p className="mb-2 text-xs text-muted-foreground">
            Uygulanabilir: {formatCurrency(payment.unallocated, payment.currency)} · {payment.currency} cinsinden açık faturalar
          </p>
          {open.isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
          ) : candidates.length === 0 ? (
            <p className="text-sm text-muted-foreground">Açık fatura yok. / No open invoices.</p>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {candidates.map((inv) => (
                  <tr key={inv.id} className="border-b last:border-b-0">
                    <td className="py-1.5 font-mono text-xs">{inv.invoiceNumber}</td>
                    <td className="py-1.5 text-xs text-muted-foreground">{inv.dueDate ? formatDate(inv.dueDate) : '—'}</td>
                    <td className="py-1.5 text-right tabular-nums">{formatCurrency(Number(inv.balanceDue), inv.currency)}</td>
                    <td className="py-1.5 text-right">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        max={Number(inv.balanceDue)}
                        value={draft[inv.id] ?? ''}
                        onChange={(e) => setDraft({ ...draft, [inv.id]: e.target.value })}
                        className="ml-auto h-8 w-[140px] text-right"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="mt-3 flex items-center justify-end gap-2">
            <span className="text-xs text-muted-foreground">Toplam / Total: {formatCurrency(total, payment.currency)}</span>
            <Button size="sm" variant="ghost" className="h-8" onClick={() => setApplying(false)} disabled={allocate.isPending}>
              Vazgeç / Cancel
            </Button>
            <Button size="sm" className="h-8" disabled={!valid || allocate.isPending} onClick={() => allocate.mutate({ paymentId: payment.id, allocations: list })}>
              {allocate.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Uygula / Apply
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
